// 调用日志页（Task 16.4）——
//   - 维度：时间范围 / Site_Key / IP 前缀 / challenge_id / Risk_Level
//   - 单查 ≤ 1000 条 + 分页（next_cursor）
//   - 详情：bot_score / rule_score / final_score / risk_level
//
// MVP：无单独详情页，行内展开。日志展示口径与服务端 logs handler 对齐。

import { useState } from 'react';
import { queryLogs, type CallLogEntry } from '../lib/api';

export function Logs(): JSX.Element {
  const [filters, setFilters] = useState({
    site_key: '',
    ip_prefix: '',
    challenge_id: '',
    risk_level: '' as '' | 'pass' | 'challenge' | 'deny',
  });
  const [items, setItems] = useState<CallLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  async function search(): Promise<void> {
    setLoading(true);
    setErr(null);
    try {
      const q: Record<string, unknown> = {};
      if (filters.site_key) q.site_key = filters.site_key;
      if (filters.ip_prefix) q.ip_prefix = filters.ip_prefix;
      if (filters.challenge_id) q.challenge_id = filters.challenge_id;
      if (filters.risk_level) q.risk_level = filters.risk_level;
      q.limit = 1000;
      const r = await queryLogs(q as Parameters<typeof queryLogs>[0]);
      setItems(r.items);
      setTruncated(r.truncated);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <h2>调用日志</h2>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', margin: '16px 0' }}>
        <Field label="site_key">
          <input
            style={inputStyle}
            value={filters.site_key}
            onChange={(e) => setFilters({ ...filters, site_key: e.target.value })}
            placeholder="ck_..."
          />
        </Field>
        <Field label="IP 前缀">
          <input
            style={inputStyle}
            value={filters.ip_prefix}
            onChange={(e) => setFilters({ ...filters, ip_prefix: e.target.value })}
            placeholder="8 位 base32"
          />
        </Field>
        <Field label="challenge_id">
          <input
            style={inputStyle}
            value={filters.challenge_id}
            onChange={(e) => setFilters({ ...filters, challenge_id: e.target.value })}
          />
        </Field>
        <Field label="风险等级">
          <select
            style={inputStyle}
            value={filters.risk_level}
            onChange={(e) =>
              setFilters({ ...filters, risk_level: e.target.value as typeof filters.risk_level })
            }
          >
            <option value="">全部</option>
            <option value="pass">pass</option>
            <option value="challenge">challenge</option>
            <option value="deny">deny</option>
          </select>
        </Field>
        <button onClick={search} style={primaryBtn} disabled={loading}>
          {loading ? '查询中…' : '查询'}
        </button>
      </div>

      {err && <div style={errorBox}>{err}</div>}
      {truncated && (
        <div style={{ ...errorBox, background: '#fef3c7', color: '#92400e' }}>
          结果已截断到 1000 条，请缩小时间范围或升级套餐扩大日志窗口。
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
        <thead>
          <tr>
            {['时间', 'trace_id', 'site_key', 'IP 前缀', '风险', '错误码'].map((h) => (
              <th
                key={h}
                style={{ textAlign: 'left', padding: '8px 6px', fontSize: 12, color: '#475569', borderBottom: '1px solid #e2e8f0' }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <>
              <tr
                key={it.trace_id}
                style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                onClick={() => setExpanded(expanded === it.trace_id ? null : it.trace_id)}
              >
                <td style={cellStyle}>{new Date(it.ts).toLocaleString()}</td>
                <td style={{ ...cellStyle, fontFamily: 'monospace' }}>{it.trace_id}</td>
                <td style={{ ...cellStyle, fontFamily: 'monospace' }}>{it.site_key}</td>
                <td style={{ ...cellStyle, fontFamily: 'monospace' }}>{it.ip_prefix}</td>
                <td style={cellStyle}>
                  <Pill level={it.risk_level} />
                </td>
                <td style={{ ...cellStyle, color: '#991b1b' }}>{it.error_code ?? ''}</td>
              </tr>
              {expanded === it.trace_id && (
                <tr key={it.trace_id + '-detail'}>
                  <td colSpan={6} style={{ padding: '8px 12px', background: '#f8fafc', fontSize: 12 }}>
                    <pre style={{ margin: 0 }}>
                      {JSON.stringify(it, null, 2)}
                    </pre>
                  </td>
                </tr>
              )}
            </>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Pill({ level }: { level: 'pass' | 'challenge' | 'deny' }): JSX.Element {
  const color = level === 'pass' ? '#10b981' : level === 'deny' ? '#ef4444' : '#f59e0b';
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 12,
        fontSize: 11,
        background: color + '20',
        color,
      }}
    >
      {level}
    </span>
  );
}

function Field(props: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, color: '#475569', minWidth: 140 }}>
      <span style={{ marginBottom: 4 }}>{props.label}</span>
      {props.children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 8px',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  fontSize: 13,
  boxSizing: 'border-box',
};
const primaryBtn: React.CSSProperties = {
  background: '#2563eb',
  color: 'white',
  border: 0,
  borderRadius: 6,
  padding: '6px 14px',
  fontSize: 13,
  cursor: 'pointer',
};
const cellStyle: React.CSSProperties = { padding: '6px', fontSize: 12 };
const errorBox: React.CSSProperties = {
  margin: '12px 0',
  padding: 8,
  fontSize: 12,
  color: '#991b1b',
  background: '#fee2e2',
  borderRadius: 6,
};
